import { Link } from "react-router-dom";
import { BookOpen, MessageSquareHeart, ShieldCheck, Sparkles, ArrowRight, Feather, Users, Star, Check, CheckCircle2, Heart, Lightbulb, Mail, LayoutDashboard } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import ActiveBetaBanner from "@/components/marketing/ActiveBetaBanner";
import { LogoMark } from "@/components/brand/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";
import EarlyAccessInlineCTA from "@/components/EarlyAccessInlineCTA";

const IMG = {
  // Fountain pen on handwritten manuscript — pure "author at work", warm, no
  // corporate-stock feel. Perfect emotional anchor for the hero.
  hero: "https://images.unsplash.com/photo-1455390582262-044cdead277a?w=1200&h=800&fit=crop&q=85",
  // Writer at desk with open notebook + pen — creative-process shot.
  authors: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB3cml0aW5nJTIwbm90ZWJvb2t8ZW58MHx8fHwxNzgzMjc5Mzk3fDA&ixlib=rb-4.1.0&q=85",
  // Trust-bar mini-avatars — warm, diverse, humans not stock corporates
  avatar1: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?crop=faces&fit=crop&w=200&h=200&q=85",
  avatar2: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?crop=faces&fit=crop&w=200&h=200&q=85",
  // Warm lamp-lit library corridor — beta-reader mood.
  book: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=940&q=85",
};

export default function Landing() {
  return (
    <>
      <ActiveBetaBanner />
      <SiteShell>
        <Hero />
      <BetaAccessNotice />
      <TrustBar />
      <WhyLastBookmark />
      <ForAuthors />
      <ForReaders />
      <WhatsComingNext />
      <Testimonials />
      <FinalCTA />
      </SiteShell>
    </>
  );
}

function Hero() {
  const { user } = useAuth();
  const isAuthed = Boolean(user);
  return (
    <section className="dark relative isolate overflow-hidden bg-background text-foreground">
      {/* Cabin-window hero backdrop. On mobile we tighten the crop to the
          right-side candle/lantern portion (avoids the marketplace-ish
          laptop mockup baked into the reference image) and darken the
          overlay so the copy always reads clean. Desktop keeps the wider
          panoramic crop. The `dark` class on this section forces all
          descendants to use dark-mode tokens even when the rest of the
          site is in light mode — the hero image is always a night
          scene, so text must remain warm-cream for legibility.
          `isolate` gives the section its own stacking context so our
          -z-10 backdrop + rain overlay paint above the section's own
          bg-background instead of escaping to the parent context. */}
      <div className="hero-backdrop absolute inset-0 -z-10" aria-hidden="true" />
      {/* Rainy window / glass overlay — subtle diagonal streaks + soft
          droplets rendered as CSS/SVG only (no extra image request). Part
          of the Last Bookmark brand identity: even when the reference PNG
          is barely visible under our darkening overlay, this layer keeps
          the "looking out a window during a storm" atmosphere intact.
          Desktop-only via the .hero-rain-overlay media query so mobile's
          candle-glow scene stays clean. Pointer-events off; readability
          untouched. */}
      <div className="hero-rain-overlay absolute inset-0 -z-10" aria-hidden="true" />
      <div className="container-page pt-10 pb-14 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 stagger-fade">
          <span className="chip">
            <Sparkles size={14} /> Now in Phase 1 — Founding community
          </span>
          <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            Where <span className="italic text-[hsl(var(--sb-clay))]">stories</span>
            <br /> grow together.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
            Last Bookmark brings authors and beta readers together in one simple, welcoming
            place to discover opportunities, connect before committing, and build trusted
            working relationships throughout the beta-reading process.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {isAuthed ? (
              <>
                <Link to="/dashboard" data-testid={TID.landing.heroCta} className="btn-primary">
                  <LayoutDashboard size={16} /> Go to your dashboard <ArrowRight size={16} />
                </Link>
                <Link to="/jobs" data-testid={TID.landing.heroSecondary} className="btn-ghost">
                  Browse projects
                </Link>
              </>
            ) : (
              <>
                <Link to="/register" data-testid={TID.landing.heroCta} className="btn-primary">
                  Start your journey <ArrowRight size={16} />
                </Link>
                <Link to="/login" data-testid={TID.landing.heroSecondary} className="btn-ghost">
                  I already have an account
                </Link>
              </>
            )}
          </div>
          <div className="mt-8 flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex -space-x-2">
              <img src={IMG.avatar1} className="w-8 h-8 rounded-full object-cover ring-2 ring-background" alt="" />
              <img src={IMG.avatar2} className="w-8 h-8 rounded-full object-cover ring-2 ring-background" alt="" />
              <div className="w-8 h-8 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center ring-2 ring-background text-xs font-semibold text-[hsl(var(--sb-forest-2))]">+</div>
            </div>
            <span>Built for authors and beta readers.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function BetaAccessNotice() {
  return (
    <section className="container-page pt-8 sm:pt-16 md:pt-24 pb-2 md:pb-4" data-testid="beta-access-notice">
      <div className="rounded-2xl lantern-notice p-3.5 md:p-7 mx-3 sm:mx-0 flex flex-col md:flex-row gap-3 md:gap-6 md:items-start">
        <div className="shrink-0 inline-flex items-center justify-center h-9 w-9 md:h-10 md:w-10 rounded-full bg-[hsl(var(--sb-gold))]/20 text-[hsl(var(--sb-gold-2))]">
          <Mail size={18} />
        </div>
        <div className="min-w-0 flex-1 space-y-3 md:space-y-5 text-[14px] md:text-[15px] leading-snug md:leading-relaxed">
          <div>
            <div className="lantern-notice-heading text-[11px] mb-1.5">Beta access &amp; feedback</div>
            <p className="font-serif text-lg md:text-xl leading-snug">
              Want to join the active beta?
            </p>
            <p className="mt-2 text-[14.5px]">
              Last Bookmark is in beta. Approved Early Access members can
              connect with real authors and beta readers instead of demo
              profiles, post or apply to projects, and use messages and Coffee
              Chats while helping test the platform before launch.
            </p>
          </div>

          <div>
            <p className="font-serif text-base md:text-lg leading-snug">
              Request Early Access
            </p>
            <p className="mt-2">
              Create an account or sign in, then select{" "}
              <strong>Request Early Access</strong> on your dashboard and
              choose what you&rsquo;d like to test. We&rsquo;ll review your
              request and notify you when access is approved.
            </p>
            <EarlyAccessInlineCTA />
          </div>

          <div>
            <p className="font-serif text-base md:text-lg leading-snug">
              Payments during beta
            </p>
            <p className="mt-2">
              Payments are arranged directly between authors and beta readers.
              Last Bookmark{" "}
              <span className="font-semibold">
                does not process, hold, or guarantee payments
              </span>{" "}
              during beta. Integrated payments are planned for launch.
            </p>
          </div>

          <div>
            <p className="font-serif text-base md:text-lg leading-snug">
              Have feedback?
            </p>
            <p className="mt-2">
              Tell us what&rsquo;s working, what&rsquo;s confusing, or what
              you&rsquo;d like to see at{" "}
              <a
                href="mailto:hello@lastbookmark.com?subject=Last%20Bookmark%20Feedback"
                className="font-semibold underline underline-offset-4 decoration-[hsl(var(--sb-gold))] decoration-2 hover:decoration-[hsl(var(--sb-gold-2))]"
                data-testid="beta-feedback-email-link"
              >
                hello@lastbookmark.com
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    { icon: ShieldCheck, label: "Certified beta readers" },
    { icon: MessageSquareHeart, label: "Constructive feedback" },
    { icon: Users, label: "Long-term relationships" },
    { icon: BookOpen, label: "All genres welcome" },
  ];
  return (
    <div className="border-y bg-[hsl(var(--sb-cream))]">
      <div className="container-page py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-3 text-sm">
            <Icon size={18} className="text-[hsl(150_32%_20%)]" />
            <span className="text-foreground/80">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WhyLastBookmark() {
  const benefits = [
    {
      title: "Better matching",
      body: "Browse by genre, reading style, experience, availability, and project needs instead of hoping the right person notices a post.",
      icon: Users,
    },
    {
      title: "Clear project details",
      body: "Authors can share expectations, word count, turnaround time, budget, and feedback needs before anyone commits.",
      icon: BookOpen,
    },
    {
      title: "Connect before committing",
      body: "Coffee Chats help authors and beta readers ask questions, discuss fit, and feel comfortable before moving forward.",
      icon: MessageSquareHeart,
    },
    {
      title: "Trust that grows",
      body: "Profiles, reviews, certifications, community guidelines, and future secure payments help create a more accountable experience.",
      icon: ShieldCheck,
    },
  ];
  return (
    <section
      className="relative overflow-visible border-y border-[hsl(150_24%_18%)] bg-[hsl(150_28%_14%)]"
      data-testid="why-last-bookmark-section"
    >
      {/* Soft golden lantern-glow anchored at the lower seam of this section.
          Purely decorative, non-interactive, and does not affect layout or
          text readability. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-40 w-[min(1100px,90%)] h-80 z-0"
        style={{
          background:
            "radial-gradient(closest-side, hsl(36 78% 60% / 0.18), hsl(36 68% 50% / 0.08) 45%, transparent 72%)",
          filter: "blur(2px)",
        }}
        data-testid="why-last-bookmark-lantern-glow"
      />
      <div className="relative z-10 container-page py-12 md:py-24">
        <div className="max-w-2xl">
          <span className="chip">
            <Sparkles size={14} /> Why Last Bookmark
          </span>
          <h2
            className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-[hsl(40_60%_96%)]"
            data-testid="why-last-bookmark-heading"
          >
            Find the right people for your story.
          </h2>
          <p className="mt-4 md:mt-5 text-base sm:text-lg text-[hsl(42_46%_92%)] leading-relaxed">
            Whether you&rsquo;re an author searching for the right readers or a beta reader looking for your next project, Last Bookmark makes it easier to find each other, connect, and work together.
          </p>
        </div>

        <div className="mt-6 md:mt-12 grid gap-3 md:gap-4 sm:grid-cols-2 lg:grid-cols-4 px-5 sm:px-0">
          {benefits.map(({ title, body, icon: Icon }) => (
            <article
              key={title}
              className="rounded-2xl md:rounded-3xl border border-[hsl(34_22%_72%)] bg-[hsl(var(--sb-surface))] p-3.5 md:p-6 flex flex-col gap-1.5 md:gap-3 transition-shadow hover:shadow-md"
              data-testid={`why-last-bookmark-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="inline-flex items-center justify-center h-8 w-8 md:h-10 md:w-10 rounded-full bg-[hsl(var(--sb-forest))]/12 text-[hsl(var(--sb-forest))]">
                <Icon size={16} />
              </span>
              <h3 className="font-serif text-base md:text-xl leading-tight text-[hsl(var(--sb-ink))]">{title}</h3>
              <p className="text-[13px] md:text-sm leading-snug md:leading-relaxed text-[hsl(var(--sb-ink-muted))]">{body}</p>
            </article>
          ))}
        </div>

        <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p
            className="text-sm sm:text-base text-[hsl(42_46%_92%)] max-w-2xl leading-relaxed"
            data-testid="why-last-bookmark-final-line"
          >
            Social media and online reading communities can help people connect.{" "}
            <span className="font-medium text-[hsl(40_60%_96%)]">
              Last Bookmark is designed to give authors and beta readers a more structured way to work together.
            </span>
          </p>
          <Link
            to="/jobs"
            className="btn-primary inline-flex items-center gap-2 shrink-0"
            data-testid="why-last-bookmark-explore-btn"
          >
            Explore Last Bookmark <ArrowRight size={16} />
          </Link>
        </div>

        <div className="mt-5">
          <a
            href="#testimonials"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("testimonials");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--sb-gold))] underline underline-offset-4 decoration-[hsl(var(--sb-gold))]/60 hover:decoration-[hsl(var(--sb-gold))] transition"
            data-testid="why-last-bookmark-testimonials-link"
          >
            See what early members are saying about Last Bookmark
            <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}

function ForAuthors() {
  return (
    <section id="authors" data-testid={TID.landing.forAuthors} className="container-page py-10 md:py-24 grid md:grid-cols-12 gap-6 md:gap-10 items-center">
      <div className="md:col-span-6 order-2 md:order-1">
        <div className="relative rounded-2xl md:rounded-[2rem] overflow-hidden border mx-auto w-[94%] sm:w-full">
          <img src={IMG.authors} alt="Author writing" className="w-full h-[240px] sm:h-[320px] md:h-[440px] object-cover" />
        </div>
      </div>
      <div className="md:col-span-6 order-1 md:order-2">
        <span className="chip"><Feather size={14} /> For Authors</span>
        <h2 className="mt-5 font-serif text-4xl md:text-5xl tracking-tight">
          Find the reader your book has been waiting for.
        </h2>
        <p className="mt-5 text-muted-foreground leading-relaxed">
          Post your project. Discover beta readers who love your genre. Communicate
          directly. Secure platform payments planned for launch. Build a shortlist of readers you'll
          come back to book after book.
        </p>
        <ul className="mt-6 space-y-3 text-sm">
          {["Rich search filters — genre, spice, word count, turnaround", "Structured questionnaires or free-form Google Docs feedback", "Secure payment protection planned for paid projects", "Save favorite readers and authors you'd like to work with again"].map((f) => (
            <li key={f} className="flex gap-3 items-start">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[hsl(var(--sb-clay))]" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ForReaders() {
  return (
    <section id="readers" data-testid={TID.landing.forReaders} className="container-page py-10 md:py-24 grid md:grid-cols-12 gap-6 md:gap-10 items-center">
      <div className="md:col-span-6">
        <span className="chip"><BookOpen size={14} /> For Beta Readers</span>
        <h2 className="mt-5 font-serif text-4xl md:text-5xl tracking-tight">
          Turn the books you love into a career you're proud of.
        </h2>
        <p className="mt-5 text-muted-foreground leading-relaxed">
          Build a public profile, earn a Quality Score, get certified, and
          become the reader authors save to their favorites. Last Bookmark rewards
          thoughtful, thorough, kind feedback.
        </p>
        <ul className="mt-6 space-y-3 text-sm">
          {["Public profile with published books, badges & reviews", "Category ratings: communication, thoroughness, professionalism", "Last Bookmark Certified program", "Direct chat & Coffee Chats with authors"].map((f) => (
            <li key={f} className="flex gap-3 items-start">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[hsl(var(--sb-clay))]" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-6">
        <div className="relative rounded-2xl md:rounded-[2rem] overflow-hidden border mx-auto w-[94%] sm:w-full">
          <img src={IMG.book} alt="Cozy library with warm reading lamp" className="w-full h-[240px] sm:h-[320px] md:h-[440px] object-cover" />
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const items = [
    {
      icon: Star,
      badge: "Early Beta Tester",
      badgeTone: "clay",
      quote:
        "I can tell how much time, effort, and care you've put into building it. It already has a solid foundation, and I'm glad I had the opportunity to explore it. Wishing you all the best as you continue refining it before launch!",
      name: "Progress Author Growth",
      role: "Early Beta Tester",
    },
    {
      icon: Lightbulb,
      badge: "Early Beta Tester",
      badgeTone: "forest",
      quote:
        "Last Bookmark felt intuitive from the moment I started using it, with an interface that balanced simplicity and utility incredibly well!",
      name: "Finley",
      role: "Early Beta Tester",
    },
    {
      icon: Heart,
      badge: "Early Community Support",
      badgeTone: "clay",
      quote:
        "This looks like an amazing space to get (and give) solid feedback without wondering if the person is going to respect your work or just run away with it. Count me in for any trials!",
      name: "Barbara Lima Madsen",
      role: "Early Community Supporter",
    },
  ];
  const toneClass = {
    clay: "bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay))] border-[hsl(var(--sb-clay))]/25",
    forest:
      "bg-[hsl(var(--sb-forest))]/10 text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest))]/25",
  };
  return (
    <section
      id="testimonials"
      className="container-page py-8 md:py-24 scroll-mt-24"
      data-testid="landing-testimonials"
    >
      <div className="max-w-3xl">
        <span className="chip inline-flex items-center gap-1.5">
          <MessageSquareHeart size={14} className="text-[hsl(var(--sb-clay))]" />
          Real Feedback from Our Early Beta Community
        </span>
        <h2 className="mt-5 font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
          Real notes from real early testers.
        </h2>
        <p className="mt-5 text-base md:text-[17px] text-muted-foreground leading-relaxed">
          Last Bookmark is currently in early beta. These comments come from
          real early testers who explored the platform and shared feedback with
          permission. Every suggestion helps shape what we&apos;re building.
        </p>
      </div>

      <div className="mt-8 md:mt-10 grid md:grid-cols-3 gap-4 md:gap-6 stagger-fade px-5 sm:px-0">
        {items.map((t) => {
          const Icon = t.icon;
          return (
            <figure
              key={`${t.name}-${t.badge}-${t.quote.slice(0, 12)}`}
              className="card-soft flex flex-col h-full !p-4 md:!p-8"
              data-testid="landing-testimonial-card"
            >
              <div
                className={`self-start inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${toneClass[t.badgeTone]}`}
              >
                <Icon size={12} />
                {t.badge}
              </div>
              <blockquote className="mt-3 md:mt-5 font-serif text-[1.05rem] md:text-[1.4rem] leading-snug flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-3 md:mt-6 pt-3 md:pt-5 border-t border-border/60">
                <div className="text-sm font-medium">— {t.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {t.role}
                </div>
              </figcaption>
            </figure>
          );
        })}
      </div>

      <p
        className="mt-8 text-xs md:text-sm text-muted-foreground italic max-w-3xl"
        data-testid="landing-testimonials-disclaimer"
      >
        These are genuine comments shared by members of our early beta community
        and are displayed with permission or anonymously.
      </p>
    </section>
  );
}

function WhatsComingNext() {
  const items = [
    "Enhanced messaging between authors and beta readers",
    "More profile customization",
    "Improved project management tools",
    "Community-requested features",
  ];
  return (
    <section
      className="container-page pb-10 md:pb-16"
      data-testid="landing-whats-coming-next"
    >
      <div className="relative overflow-hidden rounded-2xl md:rounded-[2rem] border border-border bg-[hsl(var(--sb-cream))] mx-3 sm:mx-0 px-4 py-5 md:px-16 md:py-16">
        {/* Subtle branded backdrop — soft radial highlights so the section
            reads warm without competing with FinalCTA. */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[hsl(var(--sb-clay))]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[hsl(var(--sb-forest))]/10 blur-3xl" />

        <div className="relative grid md:grid-cols-2 gap-4 md:gap-14 items-start stagger-fade">
          <div>
            <span className="chip inline-flex items-center gap-1.5" data-testid="whats-coming-next-eyebrow">
              <Sparkles size={14} className="text-[hsl(var(--sb-clay))]" />
              What&apos;s Coming Next
            </span>
            <h2 className="mt-3 md:mt-5 font-serif text-2xl md:text-5xl tracking-tight leading-[1.05]">
              We&apos;re just getting started.
            </h2>
            <p className="mt-2 md:mt-5 text-[14.5px] md:text-[17px] text-muted-foreground leading-snug md:leading-relaxed max-w-xl">
              Last Bookmark is active and growing through beta. Thank you for
              being one of our early members and helping shape the platform. We&apos;re
              continuously improving the experience and building new features
              based on community feedback.
            </p>
          </div>

          <div>
            <ul
              className="space-y-1.5 md:space-y-3.5 stagger-fade md:mx-0"
              data-testid="whats-coming-next-list"
            >
              {items.map((label) => (
                <li
                  key={label}
                  className="group flex items-center gap-2.5 md:gap-3 rounded-lg md:rounded-xl border border-border/70 bg-background/60 backdrop-blur-sm px-3 py-1.5 md:px-4 md:py-3.5 transition-all duration-300 hover:border-[hsl(var(--sb-forest))]/40 hover:bg-background hover:-translate-y-0.5"
                >
                  <span className="shrink-0 inline-flex h-5 w-5 md:h-6 md:w-6 items-center justify-center rounded-full bg-[hsl(var(--sb-forest))] text-[hsl(40_60%_96%)] group-hover:bg-[hsl(var(--sb-forest-2))] transition-colors duration-300">
                    <ArrowRight size={12} />
                  </span>
                  <span className="text-[13px] md:text-[15px] leading-snug font-medium text-foreground">
                    {label}
                  </span>
                </li>
              ))}
            </ul>

            <div
              className="mt-3 md:mt-8 flex items-start gap-2.5 md:gap-3 rounded-lg md:rounded-xl border border-[hsl(var(--sb-clay))]/25 bg-[hsl(var(--sb-clay))]/[0.06] px-3 py-2 md:px-5 md:py-4"
              data-testid="whats-coming-next-footnote"
            >
              <span className="mt-0.5 shrink-0 inline-flex h-6 w-6 md:h-7 md:w-7 items-center justify-center rounded-full bg-[hsl(var(--sb-clay))]/15 text-[hsl(var(--sb-clay))]">
                <Heart size={13} />
              </span>
              <p className="text-[13px] md:text-[15px] leading-snug md:leading-relaxed text-foreground/85">
                <span className="font-medium">Have an idea or suggestion?</span>{" "}
                We&apos;d love to hear from you! Your feedback is helping build a
                better place for authors and beta readers.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function FinalCTA() {
  const { user } = useAuth();
  const isAuthed = Boolean(user);
  return (
    <section className="container-page pb-12 md:pb-24">
      <div className="relative rounded-2xl md:rounded-[2rem] overflow-hidden bg-[hsl(var(--sb-cream))] text-[hsl(150_32%_14%)] mx-3 sm:mx-0 p-6 md:p-16">
        <div className="max-w-2xl">
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight">
            Better feedback. Better books. Better careers.
          </h2>
          <p className="mt-3 md:mt-4 text-[hsl(150_20%_28%)] leading-snug md:leading-relaxed">
            Join the founding community of Last Bookmark and help us shape a
            marketplace built for the long game.
          </p>
          <div className="mt-6 md:mt-8 flex flex-wrap gap-3">
            {isAuthed ? (
              <>
                <Link to="/dashboard" data-testid={TID.landing.finalCta} className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[hsl(var(--sb-clay))] hover:bg-[hsl(var(--sb-clay-2))] text-white text-sm font-medium transition-colors">
                  <LayoutDashboard size={16} /> Open your dashboard <ArrowRight size={16} />
                </Link>
                <Link to="/jobs" className="inline-flex items-center gap-2 rounded-full px-6 py-3 border border-[hsl(150_20%_28%)]/30 text-[hsl(150_32%_14%)] hover:bg-[hsl(150_32%_14%)]/5 text-sm font-medium transition-colors">
                  Browse projects
                </Link>
              </>
            ) : (
              <>
                <Link to="/register" data-testid={TID.landing.finalCta} className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[hsl(var(--sb-clay))] hover:bg-[hsl(var(--sb-clay-2))] text-white text-sm font-medium transition-colors">
                  Create free account <ArrowRight size={16} />
                </Link>
                <Link to="/login" className="inline-flex items-center gap-2 rounded-full px-6 py-3 border border-[hsl(150_20%_28%)]/30 text-[hsl(150_32%_14%)] hover:bg-[hsl(150_32%_14%)]/5 text-sm font-medium transition-colors">
                  Explore demo
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
